/* eslint-disable max-classes-per-file */
import { EventEmitter } from '@angular/core';
import ShellAPI from 'a365-shell-api';
import { Application } from 'app/application';
import { AuxiliaryLoggerService } from 'app/logger/auxiliary-logger.service';
import { I_LOGGER } from 'app/logger/ILogger';
import { ActionFailedEventRow, ActionSucceedEventRow } from 'app/logger/logger.action-result-event';
import { EventRow } from 'app/logger/logger.event-row';
import { MetricRow } from 'app/logger/logger.metric-row';
import { isDevEnvironment } from 'environments/environment.util';
import { TreeItem } from '../services/tree/treeItem';
import { IDisposable } from './common';

/**
 * Log a user action or other occurrence.
 * @param name Identifies the event.
 * @param properties Map of string to string: Additional data used to filter events in the portal. Defaults to empty.
 * @param measurements Map of string to number: Metrics associated with this page, displayed in Metrics Explorer on the portal. Defaults to empty.
 */
export function postEvent(extensionId: string, name?: string, properties?: { [name: string]: string }, measurements?: { [key: string]: number }): void {
    try {
        const logger = Application.injector.get(I_LOGGER);
        logger.logEvent(new EventRow(name, { extensionId, ...properties }, measurements));
    } catch (err) {
        // Only showing error in dev env.
        if (isDevEnvironment()) {
            throw err;
        }
    }
}

/**
 * Log a positive numeric value that is not associated with a specific event. Typically used to send regular reports of performance indicators.
 *
 * @param name  A string that identifies the metric. In the portal, you can select metrics for display by name.
 * @param average Either a single measurement, or the average of several measurements. Should be >=0 to be correctly displayed.
 * @param sampleCount Count of measurements represented by the average. Defaults to 1. Should be >=1.
 * @param min The smallest measurement in the sample. Defaults to the average. Should be >= 0.
 * @param max  The largest measurement in the sample. Defaults to the average. Should be >= 0.
 * @param properties Map of string to string: Additional data used to filter events in the portal.
 */
export function postMetric(extensionId: string, name: string, average: number, sampleCount?: number, min?: number, max?: number, properties?: { [name: string]: string }): void {
    try {
        const logger = Application.injector.get(I_LOGGER);
        logger.logMetric(new MetricRow(name, average, sampleCount, min, max, { extensionId, ...properties }));
    } catch (err) {
        // Only showing error in dev env.
        if (isDevEnvironment()) {
            throw err;
        }
    }
}

export function postTrace(extensionId: string, componentName: string, message?: string, properties?: { [name: string]: string }): void {
    try {
        const logger = Application.injector.get(I_LOGGER);
        logger.log(componentName, message, { extensionId, ...properties });
    } catch (err) {
        // Only showing error in dev env.
        if (isDevEnvironment()) {
            throw err;
        }
    }
}

export function postWarning(extensionId: string, componentName: string, message?: string, properties?: { [name: string]: string }): void {
    try {
        const logger = Application.injector.get(I_LOGGER);
        logger.warn(componentName, message, { extensionId, ...properties });
    } catch (err) {
        // Only showing error in dev env.
        if (isDevEnvironment()) {
            throw err;
        }
    }
}

export function postError(extensionId: string, componentName: string, error?: Error | string, properties?: { [name: string]: string }): void {
    try {
        const logger = Application.injector.get(I_LOGGER);
        logger.error(componentName, error, { extensionId, ...properties });
    } catch (err) {
        // Only showing error in dev env.
        if (isDevEnvironment()) {
            throw err;
        }
    }
}

/**
 * Log the action result event, the action result will be reported as a Geneva metric
 * It will be used together with reportActionFailed to calculate the success rate of an action
 * You can create Geneva monitor to monitor the status of an action
 * @param action The name of action needs to be tracked
 * @param properties Other properties needs to posted
 */
export function reportActionSucceed(action: string, properties?: { [name: string]: string }): void {
    try {
        const logger = Application.injector.get(I_LOGGER);
        logger.logEvent(new ActionSucceedEventRow(action, properties));
    } catch (err) {
        // Only showing error in dev env.
        if (isDevEnvironment()) {
            throw err;
        }
    }
}

/**
 * Log the action result event, the action result will be reported as a Geneva metric
 * It will be used together with reportActionSucceed to calculate the success rate of an action
 * You can create Geneva monitor to monitor the status of an action
 * @param action The name of action needs to be tracked
 * @param error The error of failed action
 * @param properties Other properties needs to posted
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function reportActionFailed(action: string, error: any, properties?: { [name: string]: string }): void {
    try {
        const logger = Application.injector.get(I_LOGGER);
        logger.logEvent(new ActionFailedEventRow(action, error, properties));
    } catch (err) {
        // Only showing error in dev env.
        if (isDevEnvironment()) {
            throw err;
        }
    }
}

/**
 * Represents the telemetry producer for a specific extension.
 */
export interface ITelemetryClient<TEventName extends string = string, TMetricName extends string = string, TComponentName extends string = string> {
    event(componentName: TComponentName, eventName: TEventName, properties?: { [name: string]: string }, measurements?: { [key: string]: number }): void;
    metric(componentName: TComponentName, metricName: TMetricName, average: number, sampleCount?: number, min?: number, max?: number, properties?: { [name: string]: string }): void;
    /**
     * Performs production environment assertion. Assertion failure will be captured by `event`.
     * @param predicateValue value of the assertion. Values that satisfies `!!predicateValue === true` will pass the assertion.
     * @param message optional message to provide when the assertion fails.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    assert(predicateValue: any, message?: string, componentName?: TComponentName): void;
    trace(componentName: TComponentName, message?: string, properties?: { [name: string]: string }): void;
    warning(componentName: TComponentName, message?: string, properties?: { [name: string]: string }): void;
    error(componentName: TComponentName, error?: Error | string, properties?: { [name: string]: string }): void;
    /**
     * Gets a telemetry client for a specific component inside the current extesion.
     * @param componentName name of the component.
     */
    ofComponent(componentName: TComponentName): IComponentTelemetryClient<TEventName, TMetricName>;
}

/**
 * Represents the telemetry producer for a specific component.
 */
export interface IComponentTelemetryClient<TEventName extends string = string, TMetricName extends string = string> {
    event(eventName: TEventName, properties?: { [name: string]: string }, measurements?: { [key: string]: number }): void;
    metric(metricName: TMetricName, average: number, sampleCount?: number, min?: number, max?: number, properties?: { [name: string]: string }): void;
    /**
     * Performs production environment assertion. Assertion failure will be captured by `event`.
     * @param predicateValue value of the assertion. Values that satisfies `!!predicateValue === true` will pass the assertion.
     * @param message optional message to provide when the assertion fails.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    assert(predicateValue: any, message?: string): void;
    trace(message?: string, properties?: { [name: string]: string }): void;
    warning(message?: string, properties?: { [name: string]: string }): void;
    error(error?: Error | string, properties?: { [name: string]: string }): void;
}

class ComponentTelemetryClient implements IComponentTelemetryClient {
    public constructor(public readonly owner: ITelemetryClient, public readonly componentName: string) {
    }
    public event(eventName: string, properties?: { [name: string]: string }, measurements?: { [key: string]: number }): void {
        this.owner.event(this.componentName, eventName, properties, measurements);
    }
    public metric(metricName: string, average: number, sampleCount?: number, min?: number, max?: number, properties?: { [name: string]: string }): void {
        this.owner.metric(this.componentName, metricName, average, sampleCount, min, max, properties);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public assert(predicateValue: any, message?: string): void {
        this.owner.assert(predicateValue, message, this.componentName);
    }
    public trace(message?: string, properties?: { [name: string]: string }): void {
        this.owner.trace(this.componentName, message, properties);
    }
    public warning(message?: string, properties?: { [name: string]: string }): void {
        this.owner.warning(this.componentName, message, properties);
    }
    public error(error?: Error | string, properties?: { [name: string]: string }): void {
        this.owner.error(this.componentName, error, properties);
    }
}

class TelemetryClient implements ITelemetryClient {
    public constructor(public readonly extensionId: string) {
    }
    public event(componentName: string, eventName: string, properties?: { [name: string]: string }, measurements?: { [key: string]: number }): void {
        postEvent(this.extensionId, eventName, { componentName, ...properties }, measurements);
    }
    public metric(componentName: string, metricName: string, average: number, sampleCount?: number, min?: number, max?: number, properties?: { [name: string]: string }): void {
        postMetric(this.extensionId, metricName, average, sampleCount, min, max, { componentName, ...properties });
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public assert(predicateValue: any, message?: string, componentName?: string): void {
        if (!!predicateValue) {
            return;
        }
        if (isDevEnvironment()) {
            console.assert(false, '[%s] Assertion failure (%s). predicateValue: %o', componentName, message, predicateValue);
        }
        postEvent(this.extensionId, 'shellapi.assertionFailure', {
            componentName,
            message,
            predicateValue: predicateValue == null ? predicateValue : predicateValue.toString(),
            predicateValueType: typeof predicateValue,
            stackTrace: ShellAPI.utils.getStackTrace()
        });
    }
    public trace(componentName: string, message?: string, properties?: { [name: string]: string }): void {
        postTrace(this.extensionId, componentName, message, properties);
    }
    public warning(componentName: string, message?: string, properties?: { [name: string]: string }): void {
        postWarning(this.extensionId, componentName, message, properties);
    }
    public error(componentName: string, error?: Error | string, properties?: { [name: string]: string }): void {
        postError(this.extensionId, componentName, error, properties);
    }
    public ofComponent(componentName: string): IComponentTelemetryClient {
        // No need to cache the instances. Usually one module uses one IComponentTelemetryClient.
        return new ComponentTelemetryClient(this, componentName);
    }
}

const telemetryClients: { [extensionId: string]: ITelemetryClient } = {};

/**
 * Gets a telemetry client for the specific extension.
 * @param extensionId extension ID. This parameter will affect the produced telemetry.
 */
export function getTelemetryClient<
    TEventName extends string = string,
    TMetricName extends string = string,
    TComponentName extends string = string
>(extensionId: string): ITelemetryClient<TEventName, TMetricName, TComponentName> {
    let client = telemetryClients[extensionId];
    if (!client) {
        client = new TelemetryClient(extensionId);
        telemetryClients[extensionId] = client;
    }
    return client;
}

/* internal */ export const shellApiTelemetryClient = getTelemetryClient('ShellAPI');

export interface IShellButtonClickEventArgsBase {
    targetName: string;
    type: string;
    componentName?: string;
    targetScope: string;
    command?: string;
    targetText?: string;
}

export interface IGeneralButtonClickEventArgs extends IShellButtonClickEventArgsBase {
    type: 'general';
}

export interface IObjectExplorerButtonClickEventArgs extends IShellButtonClickEventArgsBase {
    type: 'objectExplorer';
    sideBarId: string;
}

export interface IResourceListButtonClickEventArgs extends IShellButtonClickEventArgsBase {
    type: 'resourceList';
    viewId: string;
}

export interface ITreeItemButtonClickEventArgs extends IShellButtonClickEventArgsBase {
    type: 'treeItem';
    viewId: string;
    treeItem?: TreeItem;
}

export type ShellButtonClickEventArgs
    = IGeneralButtonClickEventArgs
    | IObjectExplorerButtonClickEventArgs
    | IResourceListButtonClickEventArgs
    | ITreeItemButtonClickEventArgs;

let shellButtonClickEvent: EventEmitter<ShellButtonClickEventArgs> | undefined;

/* internal */ export function raiseShellButtonClickEvent(e: ShellButtonClickEventArgs): void {
    if (shellButtonClickEvent) {
        shellButtonClickEvent.emit(e);
    }
}

function getContainerButton(element: Element): HTMLElement | null {
    while (element) {
        if (element instanceof HTMLButtonElement
            || element instanceof HTMLInputElement && element.type === 'button'
            || element instanceof HTMLAnchorElement && element.href
            || element instanceof HTMLDivElement && element.getAttribute('role') === 'button') {
            return element;
        }
        element = element.parentElement;
    }
    return null;
}
/**
 * Raises a ShellButtonClick event based on the actual button click DOM event.
 */
/* internal */ export function raiseShellButtonClickEventFromDomEvent(e: Event, scope: string, componentName?: string): boolean {
    if (!(e.target instanceof Element)) { return false; }
    const button = getContainerButton(e.target);
    if (!button) { return false; }
    if (shellButtonClickEvent) {
        const buttonName = button.getAttribute('name') || '';
        const buttonText = button.innerText || '';
        shellButtonClickEvent.emit(
            {
                type: 'general',
                componentName,
                targetName: buttonName,
                targetScope: scope + '/#' + buttonName,
                targetText: buttonText.substr(0, 100)
            }
        );
    }
    return true;
}

/* internal */ export function isShellButtonClickEventSubscribed(): boolean {
    return !!shellButtonClickEvent;
}

/**
 * Adds a subscriber called when the shell UI element has been clicked/invoked.
 */
export function onShellButtonClick(listener: (e: ShellButtonClickEventArgs) => void): IDisposable {
    if (!shellButtonClickEvent) {
        shellButtonClickEvent = new EventEmitter();
    }
    const subscription = shellButtonClickEvent.subscribe(listener);
    return {
        dispose(): void {
            subscription.unsubscribe();
        }
    };
}

/**
 * Post event to Auxiliary Logger
 */
export async function postEventToAuxiliaryLogger(name: string, properties?: Record<string, unknown>): Promise<void> {
    try {
        const logger = Application.injector.get(AuxiliaryLoggerService);
        await logger.trackEvent(name, properties);
    } catch (err) {
        // Only showing error in dev env.
        if (isDevEnvironment()) {
            throw err;
        }
    }
}

/**
 * Flush Auxiliary Logger
 */
 export async function flushAuxiliaryLogger(): Promise<void> {
    try {
        const logger = Application.injector.get(AuxiliaryLoggerService);
        await logger.flush();
    } catch (err) {
        // Only showing error in dev env.
        if (isDevEnvironment()) {
            throw err;
        }
    }
}
